import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import CardContainer from 'src/components/layouts/CardContainer/CardContainer';
import Toggle from 'src/components/ui/Toggle/Toggle';
import Image from 'src/components/ui/Image/Image';
import Icon from 'src/components/ui/Icon/Icon';
import Badge from 'src/components/ui/Badge/Badge';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { useNavigate } from 'react-router-dom';
import styles from '../../../pages/styles/Integrations.module.scss';
import { resolveBadgeTypeForStatus } from '../../../utils/general';
import { Connector, ConnectorUpdate, useConnectorMutations, useConnectorsQuery } from '../../../services/connector';
import LoadingSkeleton from '../../ui/LoadingSkeleton/LoadingSkeleton';
import { useNavigationItems } from '../../../hooks/useNavigationItems';
import Modal from '../../layouts/Modal/Modal';

const IntegrationsListCard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [visibleDeactivateModalId, setVisibleDeactivateModalId] = useState<number | null>(null);

  const { findAccessRights } = useNavigationItems();
  const accessRights = findAccessRights('integrations');

  const { data: connectors, isLoading } = useConnectorsQuery();
  const { deactivateMutation, updateMutation } = useConnectorMutations();

  const onConfirmDeactivate = async (connectorId: number) => {
    await deactivateMutation.mutateAsync({ params: { path: { connector_id: connectorId } } });

    setVisibleDeactivateModalId(null);
  };

  const deactivate = async (connectorId: number) => {
    setVisibleDeactivateModalId(connectorId);
  };

  const activate = async (connectorId: number, connectorUpdate: ConnectorUpdate) => {
    await updateMutation.mutateAsync({
      params: {
        path: {
          connector_id: connectorId,
        },
      },
      body: connectorUpdate,
    });
  };

  const onToggleIntegration = async (event: boolean, item: Connector) => {
    if (event) {
      const connectorUpdate = {
        active: true as true,
        fulfillment_export: true,
        order_import: true,
        product_import: true,
        specific_fields: item.template?.specific_fields?.map(item => {
          return { name: item.name, value: item.value };
        }),
        status_export: true,
        stock_quantity_export: false,
        tracking_export: true,
      };
      await activate(item.id, connectorUpdate);
    } else {
      await deactivate(item.id);
    }
  };
  const navigateToIntegration = (id: number) => {
    navigate(`/integrations/connectors/${id}`);
  };

  return (
    <CardContainer className={classNames(styles.root, 'touch-none w-full')} label={t('integrations.all_integrations')}>
      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <div className="flex flex-col w-full">
          {connectors?.items?.map(item => (
            <div
              key={item.id}
              className="flex items-center border-subtle-bottom last:border-0 py-11 md:h-[76px] gap-[12px]">
              <div className="hidden md:flex">
                <Toggle checked={!!item.active} onToggle={event => onToggleIntegration(event, item)} />
              </div>
              <div className="flex flex-1 min-w-0 flex-col md:flex-row items-start md:items-center gap-[12px]">
                <div className="w-[40px] h-[40px] flex justify-center items-center">
                  <Image
                    imageUrl={item.template?.channel?.logo_url ?? null}
                    alt="integration"
                    fallbackComponent={<Icon name="package" width={20} height={20} />}
                  />
                </div>
                <div className="flex flex-col gap-6 flex-1 min-w-0">
                  <span className="typo-text-14--bold truncate overflow-hidden text-ellipsis whitespace-nowrap">
                    {item.name}
                    {item.host ? ` (${item.host})` : ''}
                  </span>
                  <Badge
                    label={item.active ? t('integrations.active') : t('integrations.add_and_activate')}
                    expanded
                    showDot
                    dotFilled
                    type={resolveBadgeTypeForStatus(item.active ? 'successful' : 'inactive')}
                  />
                </div>
              </div>
              <div className="flex flex-col justify-between items-end gap-26 md:gap-0">
                <div className="flex md:hidden">
                  <Toggle
                    disabled={!accessRights?.read_write}
                    checked={!!item.active}
                    onToggle={event => onToggleIntegration(event, item)}
                  />
                </div>
                <Button
                  onClick={() => navigateToIntegration(item.id)}
                  className="flex-shrink-0 whitespace-nowrap"
                  title={t('general.view_details')}
                  tone={ButtonToneProps.SECONDARY}
                />
              </div>
            </div>
          ))}
        </div>
      )}
      <Modal isOpen={visibleDeactivateModalId !== null} onClose={() => setVisibleDeactivateModalId(null)}>
        <div className="max-w-460">
          <div className="border-subtle-bottom p-32">
            <h4 className="mb-12 typo-title-24">{t('integrations.deactivate_api')}</h4>
            <p className="typo-text-16 text-faded">{t('integrations.deactivate_api_text')}</p>
          </div>
          <div className="border-subtle-bottom py-16 px-24 flex justify-end">
            <Button
              className="mr-12"
              onClick={() => setVisibleDeactivateModalId(null)}
              tone={ButtonToneProps.SECONDARY}
              title={t('general.cancel')}
            />
            <Button
              onClick={() => {
                if (visibleDeactivateModalId !== null) {
                  onConfirmDeactivate(visibleDeactivateModalId);
                }
              }}
              tone={ButtonToneProps.PRIMARY_BLUE}
              title={t('general.confirm')}
            />
          </div>
        </div>
      </Modal>
    </CardContainer>
  );
};

export default IntegrationsListCard;
